/**
 * Schemas for the in-app Excel/CSV/TSV import feature (Backlog #06).
 *
 * Phase 1: preview — upload a file, parse it, return the first 50 rows
 *   + an auto-detected column mapping + a short-lived fileToken.
 *
 * Phase 2: commit — POST /api/imports/commit with fileToken + mapping;
 *   writes rows to landlords/units/landlord_phones; returns a per-run
 *   summary with counts + first 100 errors.
 */
import { z } from 'zod';
// ─── Known system fields that imported columns can be mapped to ──────────────
export const KNOWN_FIELDS = [
    { key: 'name', label: 'Owner Name', match: ['owner', 'name', 'landlord'] },
    { key: 'mobile', label: 'Mobile', match: ['mobile', 'phone', 'tel', 'contact'] },
    { key: 'unit_no', label: 'Unit Number', match: ['unit', 'apt', 'apartment'] },
    { key: 'building', label: 'Building', match: ['building', 'project', 'tower'] },
    { key: 'area_name', label: 'Area', match: ['area', 'location', 'community'] },
];
// ─── Column mapping ──────────────────────────────────────────────────────────
/**
 * Column mapping: each key is a system field, the value is the matched
 * spreadsheet header (the original column name from the file).
 *
 * All fields are optional — auto-detection may fail and the user picks
 * manually in the UI.
 */
export const ColumnMappingSchema = z.object({
    name: z.string().optional(),
    mobile: z.string().optional(),
    unit_no: z.string().optional(),
    building: z.string().optional(),
    area_name: z.string().optional(),
});
// ─── Preview response ────────────────────────────────────────────────────────
export const ImportPreviewResponseSchema = z.object({
    /** Total rows in the file (excluding the header). */
    totalRows: z.number().int().nonnegative(),
    /** Up to first 50 data rows (each is a record keyed by header). */
    sampleRows: z.array(z.record(z.string(), z.string())),
    /** Column headers as found in the file, in order. */
    headers: z.array(z.string()),
    /** Auto-detected mapping of system field → spreadsheet header. */
    mapping: ColumnMappingSchema,
    /**
     * Short-lived (15 min) token referring to the parsed data. Phase 2 will
     * exchange this for the actual commit. For Phase 1 it just expires unused.
     */
    fileToken: z.string().uuid(),
});
// ─── Commit request ───────────────────────────────────────────────────────────
export const ImportCommitRequestSchema = z.object({
    fileToken: z.string().uuid(),
    mapping: ColumnMappingSchema,
});
// ─── Commit response ──────────────────────────────────────────────────────────
export const ImportSkippedCountsSchema = z.object({
    no_name: z.number().int().nonnegative(),
    junk_name: z.number().int().nonnegative(),
    no_unit: z.number().int().nonnegative(), // unit_no present but building missing
    duplicate_unit: z.number().int().nonnegative(), // unit already exists on landlord
});
export const ImportRowErrorSchema = z.object({
    rowIndex: z.number().int().nonnegative(),
    message: z.string(),
});
export const ImportCommitResponseSchema = z.object({
    totalRows: z.number().int().nonnegative(),
    created: z.number().int().nonnegative(),
    merged: z.number().int().nonnegative(),
    skipped: ImportSkippedCountsSchema,
    errors: z.array(ImportRowErrorSchema),
});
//# sourceMappingURL=imports.js.map